/**
 * EmailDocModal — picker + compose + send for KB doc email.
 *
 * Phase B2 of DR-2026-171 / SPEC-EMAIL-002.
 *
 * Single staged-list pattern per operator preference (SPEC-EMAIL-002 §7):
 *   - Top: registered-recipients list (checkbox) + ad-hoc input
 *   - Middle: combined "Recipients for this send" staged list
 *   - Bottom: subject + cover-note + send
 *
 * Backend: POST /@docs/email-send (SPEC-EMAIL-002 §4)
 *
 * DR-2026-171 v0.4.0 Phase E (E6 / SPEC-EMAIL-002 v0.2.0 §7): one line under
 * the compose fields names what the send will attach — the Markdown source
 * always, and the PDF rendering sibling when the view route reports it ready.
 * A stale rendering (doc.pdf_rendering.state === 'stale') shows a red line
 * with both versions and DISABLES Send, because the route would answer 409.
 * The state comes from GET /@docs/view (the same helper the send route
 * decides with), so the modal and the route cannot disagree.
 */

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Input,
  Icon,
  Label,
  List,
  Message,
  Segment,
  Loader,
} from 'semantic-ui-react';
import { getApiPath, fetchApi } from '../../api';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX_RECIPIENTS = 20;
const MAX_SUBJECT = 200;
const MAX_COVER = 2000;

const EmailDocModal = ({ open, onClose, doc }) => {
  const [registered, setRegistered] = useState([]);
  const [loadingReg, setLoadingReg] = useState(false);
  const [regError, setRegError] = useState(null);
  const [search, setSearch] = useState('');

  // Staged list: { type: 'registered'|'ad_hoc', recipient_id?, email, display_name? }
  const [staged, setStaged] = useState([]);

  // Ad-hoc input fields
  const [adHocEmail, setAdHocEmail] = useState('');
  const [adHocName, setAdHocName] = useState('');
  const [adHocError, setAdHocError] = useState(null);

  // Compose fields
  const [subject, setSubject] = useState('');
  const [coverNote, setCoverNote] = useState('');

  // Send state
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendResult, setSendResult] = useState(null);

  // Reset on open/doc change
  useEffect(() => {
    if (!open) return;
    setStaged([]);
    setAdHocEmail('');
    setAdHocName('');
    setAdHocError(null);
    setCoverNote('');
    setSendError(null);
    setSendResult(null);
    setSearch('');
    setSubject(
      doc
        ? `[${doc.document_type || doc.doc_id?.split('-')[0] || 'DOC'}] ${doc.title} (v${doc.version})`
        : '',
    );
  }, [open, doc]);

  // Load registered recipients on open
  useEffect(() => {
    if (!open) return;
    setLoadingReg(true);
    setRegError(null);
    fetchApi(`${getApiPath()}/@qms/email-recipients?active=true&page_size=200`)
      .then((json) => {
        setRegistered(json.items || []);
        setLoadingReg(false);
      })
      .catch((err) => {
        setRegError(err.message || String(err));
        setLoadingReg(false);
      });
  }, [open]);

  const isStaged = (type, idOrEmail) =>
    staged.some((s) =>
      type === 'registered'
        ? s.type === 'registered' && s.recipient_id === idOrEmail
        : s.type === 'ad_hoc' && s.email === idOrEmail,
    );

  const toggleRegistered = (row) => {
    if (isStaged('registered', row.id)) {
      setStaged(staged.filter((s) => !(s.type === 'registered' && s.recipient_id === row.id)));
    } else {
      if (staged.length >= MAX_RECIPIENTS) return;
      setStaged([
        ...staged,
        {
          type: 'registered',
          recipient_id: row.id,
          email: row.email,
          display_name: row.display_name,
        },
      ]);
    }
  };

  const addAdHoc = () => {
    setAdHocError(null);
    const e = adHocEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) {
      setAdHocError('Invalid email format');
      return;
    }
    if (staged.length >= MAX_RECIPIENTS) {
      setAdHocError(`Max ${MAX_RECIPIENTS} recipients per send`);
      return;
    }
    if (isStaged('ad_hoc', e)) {
      setAdHocError('Already in staged list');
      return;
    }
    setStaged([
      ...staged,
      { type: 'ad_hoc', email: e, display_name: adHocName.trim() || null },
    ]);
    setAdHocEmail('');
    setAdHocName('');
  };

  const removeStaged = (index) => {
    setStaged(staged.filter((_, i) => i !== index));
  };

  // DR-2026-171 v0.4.0 E6: what this send will attach. Filenames follow the
  // route's pattern ({slug-leaf}_v{version}_{YYYY-MM-DD}.md / .pdf, UTC date).
  const rendering = doc?.pdf_rendering || null;
  const renderingReady = rendering?.state === 'ready';
  const renderingStale = rendering?.state === 'stale';
  const slugLeaf = doc?.slug ? doc.slug.split('/').pop() : '';
  const sendDate = new Date().toISOString().slice(0, 10);
  const mdFilename = doc ? `${slugLeaf}_v${doc.version || '0.0.0'}_${sendDate}.md` : '';
  const pdfFilename = mdFilename.replace(/\.md$/, '.pdf');

  const filteredRegistered = registered.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.email.toLowerCase().includes(s) ||
      (r.display_name || '').toLowerCase().includes(s) ||
      (r.notes || '').toLowerCase().includes(s)
    );
  });

  const submit = () => {
    setSendError(null);
    setSendResult(null);
    if (staged.length < 1) {
      setSendError('Add at least one recipient');
      return;
    }
    setSending(true);
    const registered_recipient_ids = staged
      .filter((s) => s.type === 'registered')
      .map((s) => s.recipient_id);
    const ad_hoc_recipients = staged
      .filter((s) => s.type === 'ad_hoc')
      .map((s) => ({ email: s.email, display_name: s.display_name }));

    fetchApi(`${getApiPath()}/@docs/email-send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slug: doc.slug,
        registered_recipient_ids,
        ad_hoc_recipients,
        subject,
        cover_note: coverNote || null,
      }),
    })
      .then((res) => {
        setSending(false);
        setSendResult(res);
        // If everything sent, close after a brief moment so operator sees the count
        if (res.summary?.failed === 0) {
          setTimeout(() => {
            onClose();
          }, 1200);
        }
      })
      .catch((err) => {
        setSending(false);
        setSendError(err.message || String(err));
      });
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>Email this doc</Modal.Header>
      <Modal.Content scrolling>
        {sendResult && sendResult.summary.failed === 0 && (
          <Message positive>
            <Message.Header>Sent to {sendResult.summary.sent} recipient{sendResult.summary.sent === 1 ? '' : 's'}</Message.Header>
            <p>Closing…</p>
          </Message>
        )}
        {sendResult && sendResult.summary.failed > 0 && (
          <Message warning>
            <Message.Header>
              {sendResult.summary.sent} sent, {sendResult.summary.failed} failed
            </Message.Header>
            <List divided>
              {sendResult.recipients.map((r, i) => (
                <List.Item key={i}>
                  <Icon
                    name={r.status === 'sent' ? 'check circle' : 'times circle'}
                    color={r.status === 'sent' ? 'green' : 'red'}
                  />
                  {r.email}
                  {r.error && <span style={{ color: '#900', marginLeft: '0.5em' }}>— {r.error}</span>}
                </List.Item>
              ))}
            </List>
          </Message>
        )}
        {sendError && (
          <Message negative onDismiss={() => setSendError(null)}>
            <Message.Header>Send failed</Message.Header>
            <p>{sendError}</p>
          </Message>
        )}

        {/* === Recipients picker === */}
        <Form>
          <Form.Field>
            <label>Registered recipients</label>
            <Input
              placeholder="Search email, name, or notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon="search"
              style={{ marginBottom: '0.5em' }}
            />
            {regError && <Message size="tiny" negative>{regError}</Message>}
            {loadingReg ? (
              <Loader active inline size="small" />
            ) : filteredRegistered.length === 0 ? (
              <p style={{ color: '#999', fontStyle: 'italic' }}>
                {search ? 'No matches.' : 'No registered recipients yet — add via /qms/recipients.'}
              </p>
            ) : (
              <Segment style={{ maxHeight: '200px', overflowY: 'auto', padding: '0.5em' }}>
                <List divided>
                  {filteredRegistered.map((r) => {
                    const checked = isStaged('registered', r.id);
                    return (
                      <List.Item
                        key={r.id}
                        style={{ cursor: 'pointer', padding: '0.4em 0.2em' }}
                        onClick={() => toggleRegistered(r)}
                      >
                        <Icon
                          name={checked ? 'check square outline' : 'square outline'}
                          color={checked ? 'blue' : undefined}
                        />
                        <List.Content>
                          <List.Header>{r.email}</List.Header>
                          {(r.display_name || r.notes) && (
                            <List.Description style={{ fontSize: '0.85em', color: '#666' }}>
                              {r.display_name}
                              {r.display_name && r.notes ? ' — ' : ''}
                              {r.notes}
                            </List.Description>
                          )}
                        </List.Content>
                      </List.Item>
                    );
                  })}
                </List>
              </Segment>
            )}
          </Form.Field>

          <Form.Field>
            <label>Add an ad-hoc recipient (not in registry)</label>
            <div style={{ display: 'flex', gap: '0.5em', alignItems: 'flex-start' }}>
              <Input
                placeholder="email@example.com"
                value={adHocEmail}
                onChange={(e) => setAdHocEmail(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAdHoc(); } }}
                style={{ flex: 2 }}
              />
              <Input
                placeholder="Display name (optional)"
                value={adHocName}
                onChange={(e) => setAdHocName(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAdHoc(); } }}
                style={{ flex: 2 }}
              />
              <Button onClick={addAdHoc} type="button" disabled={!adHocEmail}>
                <Icon name="add" /> Add
              </Button>
            </div>
            {adHocError && (
              <p style={{ color: '#900', fontSize: '0.85em', margin: '0.3em 0 0 0' }}>{adHocError}</p>
            )}
          </Form.Field>

          {/* === Staged list === */}
          <Form.Field>
            <label>
              Recipients for this send <Label circular>{staged.length}</Label>
              {staged.length >= MAX_RECIPIENTS && (
                <Label color="orange" size="small" style={{ marginLeft: '0.5em' }}>
                  max reached
                </Label>
              )}
            </label>
            {staged.length === 0 ? (
              <Segment placeholder textAlign="center" style={{ padding: '1em', minHeight: 'auto' }}>
                <p style={{ color: '#999', margin: 0 }}>
                  Select from registered above or add ad-hoc — at least one required.
                </p>
              </Segment>
            ) : (
              <Segment style={{ padding: '0.5em' }}>
                <List divided>
                  {staged.map((s, i) => (
                    <List.Item key={i} style={{ padding: '0.4em 0.2em' }}>
                      <List.Content floated="right">
                        <Button
                          icon
                          size="mini"
                          basic
                          onClick={() => removeStaged(i)}
                          aria-label="Remove"
                        >
                          <Icon name="close" />
                        </Button>
                      </List.Content>
                      <List.Content>
                        <Label
                          size="mini"
                          color={s.type === 'registered' ? 'blue' : 'grey'}
                          style={{ marginRight: '0.4em' }}
                        >
                          {s.type === 'registered' ? 'registered' : 'ad-hoc'}
                        </Label>
                        {s.email}
                        {s.display_name && (
                          <span style={{ color: '#666' }}> — {s.display_name}</span>
                        )}
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              </Segment>
            )}
          </Form.Field>

          {/* === Compose === */}
          <Form.Field>
            <label>
              Subject{' '}
              <span style={{ color: '#999', fontWeight: 'normal', fontSize: '0.85em' }}>
                ({subject.length}/{MAX_SUBJECT})
              </span>
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, MAX_SUBJECT))}
              placeholder="Email subject"
            />
          </Form.Field>

          <Form.Field>
            <label>
              Cover note{' '}
              <span style={{ color: '#999', fontWeight: 'normal', fontSize: '0.85em' }}>
                (optional, {coverNote.length}/{MAX_COVER})
              </span>
            </label>
            <Form.TextArea
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value.slice(0, MAX_COVER))}
              rows={4}
              placeholder="(Optional — operator's cover note. The snapshot disclaimer is always appended automatically.)"
            />
          </Form.Field>

          {/* === Attachments — DR-2026-171 v0.4.0 E6 === */}
          <Form.Field>
            <label>Attachments</label>
            {renderingStale ? (
              <p style={{ color: '#900', margin: 0 }}>
                <Icon name="warning sign" /> PDF rendering is stale (rendered v
                {rendering.stamped_version}, document is v{rendering.doc_version}).
                Re-render and deploy before sending.
              </p>
            ) : (
              <p style={{ margin: 0 }}>
                <Icon name="file text outline" /> {mdFilename}
                {renderingReady && (
                  <span>
                    {' '}and the PDF rendering <Icon name="file pdf outline" />
                    {pdfFilename} (v{rendering.stamped_version},{' '}
                    {Math.round(rendering.byte_length / 1024)} KB)
                  </span>
                )}
              </p>
            )}
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          primary
          onClick={submit}
          loading={sending}
          disabled={sending || staged.length < 1 || renderingStale}
          title={renderingStale ? 'The PDF rendering is stale; the route would refuse this send (409).' : undefined}
        >
          <Icon name="send" /> Send to {staged.length} recipient{staged.length === 1 ? '' : 's'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EmailDocModal;
